import type{ RouteLocationNormalized, NavigationGuardNext } from 'vue-router'
import { createRouter, createWebHashHistory } from 'vue-router'
import { useConstruction } from "@/views/construction/ConstructionComposable"
const loadConstructionHook = async (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) => {
  try {
    const { loadConstruction } = useConstruction()
    // 工事情報取得
    await loadConstruction(to.params.cloudConstructionId as string)
  } catch {
    next({ name: 'ConstructionList' }); // エラーの場合、工事一覧へ
    return
  }
  next()
}


// よく使うmeta設定
const defaultMetaProps = {
  leftHeaderButton: 'back',
  logoTitle: false,
  disableTalkLink: false,
  isBannerDisplay: false,
}

const routes = [
  { // Login
    path: '/login',
    name: 'Login',
    meta: {
      isPublic: true,
      layout: 'front-empty',
    },
    component: () => import('@/views/Login.vue')
  },
  {
    path: '/loginCooperation',
    name: 'LoginCooperation',
    component: () => import('@/views/LoginCooperation.vue'),
    meta: {
      isPublic: true,
      layout: 'front-empty',
    },
  },
  {
    path: '/loginCooperation/fromApp',
    name: 'LoginCooperationFromApp',
    component: () => import('@/views/LoginCooperationFromApp.vue'),
    meta: {
      isPublic: true,
      layout: 'front-empty',
      omitPlatformCheck: true,
    },
  },
  {
    path: '/external/xg/pp/login/construction/',
    name: 'LoginStartFromXgrabConstruction',
    component: () => import('@/views/external/xg/LoginStartFromXgrab.vue'),
    meta: {
      isPublic: true,
      layout: 'front-empty',
      omitPlatformCheck: true,
    },
  },
  {
    path: '/external/xg/pp/login/',
    name: 'LoginStartFromXgrab',
    component: () => import('@/views/external/xg/LoginStartFromXgrab.vue'),
    meta: {
      isPublic: true,
      layout: 'front-empty',
      omitPlatformCheck: true,
    },
  },
  {
    path: '/external/xg/authCode',
    name: 'HandleXgrabAuthCode',
    component: () => import('@/views/external/xg/HandleXgrabAuthCode.vue'),
    meta: {
      isPublic: true,
      layout: 'front-empty',
      omitPlatformCheck: true,
    }
  },
  {
    path: '/external/sso/login',
    name: 'HandleSsoLogin',
    component: () => import('@/views/external/sso/HandleSsoLogin.vue'),
    meta: {
      isPublic: true,
      layout: 'front-empty',
      omitPlatformCheck: true,
    }
  },
  {
    path: '/external/sso/login-result',
    name: 'SsoLoginResult',
    component: () => import('@/views/external/sso/SsoLoginResult.vue'),
    meta: {
      isPublic: true,
      layout: 'front',
      omitPlatformCheck: true,
    }
  },
  { // LoginKobito
    path: '/loginKobito/:kobitoToken',
    name: 'LoginKobito',
    meta: {
      isPublic: true,
      layout: 'front',
    },
    component: () => import('@/views/LoginKobito.vue')
  },
  { // NoSupportBrowser
    path: '/no-support-browser',
    name: 'NoSupportBrowser',
    meta: {
      isPublic: true,
      layout: 'front',
      omitPlatformCheck: true,
    },
    component: () => import('@/views/NoSupportBrowser.vue')
  },
  { // AppWebViewExited
    path: '/app',
    name: 'AppWebViewExited',
    meta: {
      isPublic: true,
      layout: 'front-empty',
      omitPlatformCheck: true,
    },
    component: () => import('@/views/AppWebViewExited.vue')
  },
  { // OpenRequestPage
    path: '/open',
    name: 'OpenRequestPage',
    component: () => import('@/views/OpenRequestPage.vue'),
  },
  { // OpenUpdateHistory
    path: '/open-update-history',
    name: 'OpenUpdateHistory',
    component: () => import('@/views/externalConnection/OpenUpdateHistory.vue'),
    meta: {
      layout: 'empty',
    },
  },
  { // OpenExternalSite
    path: '/open-external-site',
    name: 'OpenExternalSite',
    component: () => import('@/views/externalConnection/OpenExternalSite.vue'),
    meta: {
      layout: 'empty',
    },
  },
  { // OpenSupport
    path: '/open-support',
    name: 'OpenSupport',
    component: () => import('@/views/externalConnection/OpenSupport.vue'),
    meta: {
      layout: 'empty',
    },
  },
  {
    path: '/:cloudConstructionId/open-talk',
    name: 'OpenTalk',
    component: () => import('@/views/externalConnection/OpenTalk.vue'),
    meta: {
      layout: 'empty',
    },
  },
  { // InviteMember
    path: '/:cloudConstructionId/invite-member',
    name: 'InviteMember',
    component: () => import('@/views/externalConnection/InviteMember.vue'),
    meta: {
      layout: 'empty',
    },
  },
  { // ShareDaicho
    path: '/:cloudConstructionId/share-daicho',
    name: 'ShareDaicho',
    beforeEnter: loadConstructionHook,
    component: () => import('@/views/externalConnection/DaichoShare.vue'),
    meta: {
      layout: 'empty',
    },
  },
  { // AddPhoto
    path: '/:cloudConstructionId/add-photo',
    name: 'AddPhoto',
    beforeEnter: loadConstructionHook,
    component: () => import('@/views/externalConnection/AddPhoto.vue'),
    meta: {
      layout: 'empty',
    },
  },
  { // MarkupPhoto
    path: '/:cloudConstructionId/:photoKey/markup-photo',
    name: 'MarkupPhoto',
    beforeEnter: loadConstructionHook,
    component: () => import('@/views/externalConnection/MarkupPhoto.vue'),
    meta: {
      layout: 'empty',
    },
  },
  { // DownloadPhotos
    path: '/:cloudConstructionId/download-photos',
    name: 'DownloadPhotos',
    beforeEnter: loadConstructionHook,
    component: () => import('@/views/externalConnection/DownloadPhotos.vue'),
    meta: {
      layout: 'empty',
      // query.photo_keys で カンマ区切りのphotoKeyをもらう想定
    },
  },
  { // Account
    path: '/account',
    component: () => import('@/views/Account.vue'),
    children: [
      {
        path: 'request-password-reset',
        name: 'RequestPasswordResetForm',
        component: () => import('@/views/account/RequestPasswordReset/RequestPasswordResetForm.vue'),
        meta: {
          isPublic: true,
          layout: 'front',
        }
      },
      {
        path: 'request-password-reset/complete',
        name: 'RequestPasswordResetComplete',
        component: () => import('@/views/account/RequestPasswordReset/RequestPasswordResetComplete.vue'),
        meta: {
          isPublic: true,
          layout: 'front',
        }
      },
      {
        path: 'reset-password/:onetimeToken',
        name: 'ResetPasswordForm',
        component: () => import('@/views/account/ResetPasswordForm.vue'),
        meta: {
          isPublic: true,
          layout: 'front',
          omitPlatformCheck: true,
        },
      },
      {
        path: 'registration/:onetimeToken',
        name: 'Registration',
        meta: {
          isPublic: true,
          layout: 'front',
        },
        component: () => import('@/views/account/DisableRegistration.vue'),
      },
      {
        path: 'invitation/:onetimeToken',
        name: 'Invitation',
        meta: {
          isPublic: true,
          layout: 'front',
        },
        component: () => import('@/views/account/DisableInvitation.vue'),
      },
      {
        path: 'receive-kokuban-invitation/:onetimeToken',
        name: 'ReceiveKokubanInvitation',
        meta: {
          isPublic: true,
          layout: 'front',
        },
        component: () => import('@/views/account/DisableRegistration.vue'),
      },
      {
        path: 'direct-invitation/:onetimeToken',
        name: 'DirectInvitation',
        meta: {
          isPublic: true,
          layout: 'front',
          omitPlatformCheck: true,
        },
        component: () => import('@/views/account/DisableInvitation.vue'),
      },
      {
        path: 'final-registration/:onetimeToken',
        name: 'FinalRegistration',
        meta: {
          isPublic: true,
          layout: 'front',
          omitPlatformCheck: true,
        },
        component: () => import('@/views/account/FinalRegistration.vue'),
      },
      {
        path: 'change-mail/:onetimeToken',
        name: 'ChangeMail',
        meta: {
          isPublic: true,
          layout: 'front',
        },
        component: () => import('@/views/account/ChangeMail.vue'),
      },
      {
        path: 'request-pix-registration/:onetimeToken',
        name: 'RequestPixRegistration',
        meta: {
          isPublic: true,
          layout: 'front',
        },
        component: () => import('@/views/account/RequestPixRegistration.vue'),
      },
      {
        path: 'request-registration',
        name: 'RequestRegistration',
        meta: {
          isPublic: true,
          layout: 'front' ,
          omitPlatformCheck: true,
        },
        component: () => import('@/views/account/RequestRegistration/RequestRegistrationForm.vue'),
      },
      {
        path: 'request-registration/complete',
        name: 'RequestRegistrationComplete',
        meta: {
          isPublic: true,
          layout: 'front',
          omitPlatformCheck: true,
        },
        component: () => import('@/views/account/RequestRegistration/RequestRegistrationComplete.vue'),
      },
      {
        path: 'request-registration/cancel',
        name: 'RequestRegistrationCancel',
        meta: {
          isPublic: true,
          layout: 'front',
          omitPlatformCheck: true,
        },
        component: () => import('@/views/account/RequestRegistration/RequestRegistrationCancel.vue'),
      },
      {
        path: 'request-registration/error',
        name: 'RequestRegistrationError',
        meta: {
          isPublic: true,
          layout: 'front',
          omitPlatformCheck: true,
        },
        component: () => import('@/views/account/RequestRegistration/RequestRegistrationError.vue'),
      },
      {
        path: 'request-temporary-registration/kuraemon_camera/:onetimeToken',
        name: 'RequestTemporaryRegistrationKuraemonCamera',
        meta: {
          isPublic: true,
          layout: 'front',
          omitPlatformCheck: true,
        },
        component: () => import('@/views/account/RequestTemporaryRegistrationKuraemonCamera.vue'),
      },
      {
        path: 'request-temporary-registration/:fromPage',
        name: 'RequestTemporaryRegistrationFromPage',
        meta: {
          isPublic: true,
          layout: 'front',
          omitPlatformCheck: true,
        },
        component: () => import('@/views/account/RequestTemporaryRegistration.vue'),
      },
      {
        path: 'request-temporary-registration',
        name: 'RequestTemporaryRegistration',
        meta: {
          isPublic: true,
          layout: 'front',
          omitPlatformCheck: true,
        },
        component: () => import('@/views/account/RequestTemporaryRegistration.vue'),
      },
      {
        path: 'request-invitation/:onetimeToken',
        name: 'RequestInvitation',
        meta: {
          isPublic: true,
          layout: 'front',
          omitPlatformCheck: true,
        },
        component: () => import('@/views/account/RequestInvitation.vue'),
      },
      {
        path: 'account-setting',
        name: 'AccountSetting',
        component: () => import('@/views/account/AccountSetting.vue'),
        meta: {
          logoTitle: true,
          disableTalkLink: true,
        }
      },
      {
        path: 'account-plan',
        name: 'AccountPlan',
        redirect: { name: 'AccountSetting' }, // 廃止 アカウント設定にリダイレクト
      },
    ]
  },
  { // Connect
    path: '/connect',
    component: () => import('@/views/Connect.vue'),
    children: [
      {
        path: 'receive-kokuban-token/:onetimeToken',
        name: 'ReceiveKokubanToken',
        component: () => import('@/views/connect/DisableReceiveKokuban.vue'),
        meta: {
          layout: 'front',
          isPublic: true,
        }
      },
      {
        path: 'receive-kokuban/:onetimeToken',
        name: 'ReceiveKokuban',
        component: () => import('@/views/connect/DisableReceiveKokuban.vue'),
        meta: {
          layout: 'front',
        }
      },
    ],
  },
  { // CreateConstruction
    path: '/create-construction',
    name: 'CreateConstruction',
    meta: {
      backRouteFor: () => ({ name: 'ConstructionList' }),
      disableTalkLink: true,
    },
    component: () => import('@/views/construction/create/CreateConstruction.vue'),
    children: [
      {
        path: 'guideline-category',
        name: 'SelectGuidelineCategory',
        meta: {
          backRouteFor: () => ({ name: 'CreateConstruction' }),
          disableTalkLink: true,
        },
        component: () => import ('@/views/construction/create/SelectGuidelineCategory.vue'),
        children: [
          {
            path: 'guideline',
            name: 'SelectGuideline',
            meta: {
              backRouteFor: () => ({ name: 'SelectGuidelineCategory' }),
              disableTalkLink: true,
            },
            component: () => import('@/views/construction/create/SelectGuideline.vue'),
            children: [
              {
                path: 'sub-guideline',
                name: 'SelectSubGuideline',
                meta: {
                  backRouteFor: () => ({ name: 'SelectGuideline' }),
                  disableTalkLink: true,
                },
                component: () => import('@/views/construction/create/SelectSubGuideline.vue'),
                children: [
                  {
                    path: 'broad-category',
                    name: 'SelectBroadCategory',
                    meta: {
                      backRouteFor: () => ({ name: 'SelectSubGuideline'}),
                      disableTalkLink: true,
                    },
                    component: () => import('@/views/construction/create/SelectBroadCategory.vue'),
                  }
                ]
              }
            ]
          },
          {
            path: 'confirm-setting-organize-info',
            name: 'ConfirmSettingOrganizeInfo',
            meta: {
              backRouteFor: () => ({ name: 'SelectGuidelineCategory'}),
              disableTalkLink: true,
            },
            component: () => import('@/views/construction/create/ConfirmSettingOrganizeInfo.vue'),
          }
        ]
      },
      {
        path: 'copy-construction',
        name: 'SelectCopyConstruction',
        meta: {
          backRouteFor: () => ({ name: 'CreateConstruction' }),
          disableTalkLink: true,
        },
        component: () => import('@/views/construction/create/SelectCopyConstruction.vue'),
      },
      {
        path: 'photo-organizer',
        name: 'CreateConstructionPhotoOrganizer',
        meta: {
          backRouteFor: (_to: RouteLocationNormalized, from: RouteLocationNormalized) => from,
          disableTalkLink: true,
        },
        component: () => import('@/views/construction/photoOrganizer/PhotoOrganizer.vue'),
      }
    ]
  },
  { // KuraemonApps
    path: '/kuraemon-apps',
    name: 'KuraemonApps',
    component: () => import('@/views/KuraemonApps.vue'),
    meta: {
      logoTitle: true,
      disableTalkLink: true,
    },
  },
  { // DaichoView
    path: '/:cloudConstructionId/daicho-view',
    name: 'DaichoView',
    component: () => import('@/views/construction/daichoView/DaichoView.vue'),
    meta: {
      layout: 'empty',
    },
  },
  { // HaikinView
    path: '/:cloudConstructionId/haikin-view',
    name: 'HaikinView',
    component: () => import('@/views/construction/haikinView/HaikinView.vue'),
    meta: {
      layout: 'empty',
    },
  },
  { // ConstructionList
    path: '/',
    name: 'ConstructionList',
    component: () => import('@/views/ConstructionList.vue'),
    meta: {
      leftHeaderButton: 'menu',
      logoTitle: true,
      disableTalkLink: true,
      isBannerDisplay: true,
    },
    children: [
      { // Construction
        path: ':cloudConstructionId',
        component: () => import('@/views/Construction.vue'),
        children: [
          {
            path: 'photos',
            name: 'PhotoList',
            meta: {
              ...defaultMetaProps,
              backRouteFor: (route: RouteLocationNormalized) => ({ name: 'ConstructionList', query: { construction_id: route.params.cloudConstructionId } }),
            },
            component: () => import('@/views/construction/photo/PhotoList.vue'),
            children: [
              {
                path: ':photoKey',
                name: 'PhotoDetail',
                component: () => import('@/views/construction/photo/PhotoDetail.vue'),
                meta: {
                  backRouteFor: (route: RouteLocationNormalized) => ({
                    name: 'PhotoList',
                    params: { cloudConstructionId: route.params.cloudConstructionId },
                    query: {
                      ...route.query,
                      photo_key: route.params.photoKey,
                    }
                  }),
                },
              },
            ],
          },
          {
            path: 'deleted-photos',
            name: 'DeletedPhotoList',
            component: () => import('@/views/construction/photo/DeletedPhotoList.vue'),
            meta: {
              ...defaultMetaProps,
              backRouteFor: (route: RouteLocationNormalized) => ({ name: 'ConstructionList', query: { construction_id: route.params.cloudConstructionId } }),
            },
          },
          {
            path: 'kokubans',
            name: 'KokubanList',
            component: () => import('@/views/construction/kokuban/KokubanList.vue'),
            meta: {
              ...defaultMetaProps,
              backRouteFor: (route: RouteLocationNormalized) => ({ name: 'ConstructionList', query: { construction_id: route.params.cloudConstructionId } }),
            },
            children: [
              {
                path: ':kokubanHash',
                name: 'KokubanDetail',
                component: () => import('@/views/construction/kokuban/KokubanDetail.vue'),
                meta: {
                  backRouteFor: (route: RouteLocationNormalized) => ({
                    name: 'KokubanList',
                    params: { cloudConstructionId: route.params.cloudConstructionId },
                    query: {
                      ...route.query,
                      kokuban_hash: route.params.kokubanHash,
                    }
                  }),
                },
                children: [
                  {
                    path: 'photos',
                    name: 'KokubanPhotoList',
                    component: () => import('@/views/construction/kokuban/KokubanPhotoList.vue'),
                    meta: {
                      backRouteFor: (route: RouteLocationNormalized) => {
                        const kokubanQuery = route.query.kokuban_query ? JSON.parse(route.query.kokuban_query as string) : {};
                        const query = {
                          ...kokubanQuery,
                          kokuban_hash: route.params.kokubanHash,
                        };
                        return {
                          name: 'KokubanList',
                          params: { cloudConstructionId: route.params.cloudConstructionId },
                          query
                        }
                      },
                    },
                    children: [
                      {
                        path: ':photoKey',
                        name: 'KokubanPhotoDetail',
                        component: () => import('@/views/construction/kokuban/KokubanPhotoDetail.vue'),
                        meta: {
                          backRouteFor: (route: RouteLocationNormalized) => ({
                            name: 'KokubanPhotoList',
                            params: { cloudConstructionId: route.params.cloudConstructionId, kokubanHash: route.params.kokubanHash },
                            query: {
                              ...route.query,
                              photo_key: route.params.photoKey,
                            }
                          }),
                        },
                      },
                    ],
                  },
                ],
              },
            ],
          },
          {
            path: 'deleted-kokubans',
            name: 'DeletedKokubanList',
            component: () => import('@/views/construction/kokuban/DeletedKokubanList.vue'),
            meta: {
              ...defaultMetaProps,
              backRouteFor: (route: RouteLocationNormalized) => ({ name: 'ConstructionList', query: { construction_id: route.params.cloudConstructionId } }),
            },
          },
          {
            path: 'zumens',
            name: 'ZumenList',
            beforeEnter: loadConstructionHook,
            component: () => import('@/views/construction/zumen/ZumenList.vue'),
            meta: {
              ...defaultMetaProps,
              backRouteFor: (route: RouteLocationNormalized) => ({ name: 'ConstructionList', query: { construction_id: route.params.cloudConstructionId } }),
            },
            children: [
              {
                path: ':zumenId',
                name: 'ZumenPageList',
                component: () => import('@/views/construction/zumen/ZumenPageList.vue'),
                meta: {
                  backRouteFor: (route: RouteLocationNormalized) => ({
                    name: 'ZumenList',
                    params: { cloudConstructionId: route.params.cloudConstructionId },
                    query: {
                      ...route.query,
                      zumen_id: route.params.zumenId,
                    }
                  }),
                },
                children: [
                  {
                    path: ':zumenPageNumber',
                    name: 'ZumenPageDetail',
                    component: () => import('@/views/construction/zumen/ZumenPageDetail.vue'),
                    meta: {
                      backRouteFor: (route: RouteLocationNormalized) => ({
                        name: 'ZumenPageList',
                        params: { 
                          cloudConstructionId: route.params.cloudConstructionId,
                          zumenId: route.params.zumenId,
                        },
                      }),
                    },
                  }

                ],
              }
            ]
          },
          {
            path: 'deleted-zumens',
            name: 'DeletedZumenList',
            beforeEnter: loadConstructionHook,
            component: () => import('@/views/construction/zumen/DeletedZumenList.vue'),
            meta: {
              ...defaultMetaProps,
              backRouteFor: (route: RouteLocationNormalized) => ({ name: 'ConstructionList', query: { construction_id: route.params.cloudConstructionId } }),
            },
          },
          {
            path: 'bookracks',
            name: 'BookrackList',
            beforeEnter: loadConstructionHook,
            component: () => import('@/views/construction/bookrack/BookrackList.vue'),
            meta: {
              ...defaultMetaProps,
              backRouteFor: (route: RouteLocationNormalized) => ({ name: 'ConstructionList', query: { construction_id: route.params.cloudConstructionId } }),
            },
          },
          {
            // フォーム
            path: 'forms',
            name: 'FormList',
            beforeEnter: loadConstructionHook,
            component: () => import('@/views/construction/form/FormList.vue'),
            meta: {
              ...defaultMetaProps,
              backRouteFor: (route: RouteLocationNormalized) => ({ name: 'ConstructionList', query: { construction_id: route.params.cloudConstructionId } }),
            },
            children: [
              {
                path: 'create-form',
                name: 'CreateForm',
                component: () => import('@/views/construction/form/CreateForm.vue'),
                meta: {
                  backRouteFor: (route: RouteLocationNormalized) => ({ 
                    name: 'FormList', 
                    params: { cloudConstructionId: route.params.cloudConstructionId }
                  }),
                },
              },
              {
                path: ':kuraformSiteId',
                component: () => import('@/views/construction/form/FormInfo.vue'),
                children: [
                  {
                    // フォーム詳細画面
                    path: 'form-detail',
                    name: 'FormDetail',
                    component: () => import('@/views/construction/form/FormDetail.vue'),
                    meta: {
                      backRouteFor: (route: RouteLocationNormalized) => ({
                        name: 'FormList',
                        params: { cloudConstructionId: route.params.cloudConstructionId },
                      })
                    }
                  },
                  {
                    // 回答一覧画面
                    path: 'answer-list',
                    name: 'FormAnswerList',
                    component: () => import('@/views/construction/form/FormAnswerList.vue'),
                    meta: {
                      backRouteFor: (route: RouteLocationNormalized) => ({
                        name: 'FormList',
                        params: { cloudConstructionId: route.params.cloudConstructionId },
                      })
                    }
                  },
                ],
              },
            ],
          },
          {
            path: 'deleted-forms',
            name: 'DeletedFormList',
            beforeEnter: loadConstructionHook,
            component: () => import('@/views/construction/form/DeletedFormList.vue'),
            meta: {
              ...defaultMetaProps,
              backRouteFor: (route: RouteLocationNormalized) => ({ name: 'ConstructionList', query: { construction_id: route.params.cloudConstructionId } }),
            },
          },
          {
            path: 'members',
            name: 'MemberList',
            beforeEnter: loadConstructionHook,
            component: () => import('@/views/construction/member/MemberList.vue'),
            meta: {
              ...defaultMetaProps,
              backRouteFor: (route: RouteLocationNormalized, from: RouteLocationNormalized) => {
                // 旧工事は設定画面を経由せず直接メンバー一覧に遷移するため、戻り先を工事一覧にする
                if (from.matched.some(r => r.name === 'ConstructionSetting')) {
                  return { name: 'ConstructionSetting' }
                }
                return { name: 'ConstructionList', query: { construction_id: route.params.cloudConstructionId } }
              },
            },
          },
          {
            path: 'deleted-members',
            name: 'DeletedMemberList',
            beforeEnter: loadConstructionHook,
            component: () => import('@/views/construction/member/DeletedMemberList.vue'),
            meta: {
              ...defaultMetaProps,
              backRouteFor: (route: RouteLocationNormalized) => ({ name: 'ConstructionList', query: { construction_id: route.params.cloudConstructionId } }),
            },
          },
          {
            path: 'photo-editor',
            beforeEnter: loadConstructionHook,
            component: () => import('@/views/construction/photoEditor/PhotoEditor.vue'),
            meta: {
              ...defaultMetaProps,
              backRouteFor: (route: RouteLocationNormalized) => ({ name: 'ConstructionList', query: { construction_id: route.params.cloudConstructionId } }),
            },
            children: [
              {
                path: 'new-photo',
                name: 'NewPhotoEditor',
                component: () => import('@/views/construction/photoEditor/NewPhotoEditor.vue'),
                meta: {
                  backRouteFor: (route: RouteLocationNormalized) => ({ name: 'ConstructionList', query: { construction_id: route.params.cloudConstructionId } }),
                  layout: 'empty',
                },
              },
              {
                path: ':photoKey',
                name: 'UpdatePhotoEditor',
                component: () => import('@/views/construction/photoEditor/UpdatePhotoEditor.vue'),
                meta: {
                  backRouteFor: (route: RouteLocationNormalized, fromRoute: RouteLocationNormalized) => {
                    if(fromRoute.name === 'PhotoList'){
                      return {
                        name: 'PhotoList',
                        params: { ...fromRoute.params },
                        query: {
                          ...route.query,
                          photo_key: route.params.photoKey,
                        }
                      }
                    }
                    if(fromRoute.name === 'KokubanPhotoList'){
                      return {
                        name: 'KokubanPhotoList',
                        params: { ...fromRoute.params },
                        query: {
                          ...fromRoute.query,
                          photo_key: route.params.photoKey,
                        }
                      }
                    }
                    if(fromRoute.name === 'PhotoTabs'){
                      return {
                        name: 'PhotoTabs',
                        params: { ...fromRoute.params },
                        query: {
                          ...route.query,
                          photo_key: route.params.photoKey,
                        }
                      }
                    }
                    return fromRoute
                  },
                  layout: 'empty',
                },
              },
            ],
          },
          { // 工事設定
            path: 'construction-setting',
            name: 'ConstructionSetting',
            beforeEnter: loadConstructionHook,
            component: () => import('@/views/construction/setting/ConstructionSetting.vue'),
            meta: {
              ...defaultMetaProps,
              backRouteFor: (route: RouteLocationNormalized) => ({ name: 'ConstructionList', query: { construction_id: route.params.cloudConstructionId } }),
            },
            children: [
              {
                path: 'add-member-permission-setting',
                name: 'AddMemberPermissionSetting',
                component: () => import('@/views/construction/setting/AddMemberPermissionSetting.vue'),
                meta: {
                  backRouteFor: () => ({ name: 'ConstructionSetting' }),
                }
              },
              {
                path: 'kokuban-default-setting',
                name: 'KokubanDefaultSetting',
                component: () => import('@/views/construction/setting/KokubanDefaultSetting.vue'),
                meta: {
                  backRouteFor: () => ({ name: 'ConstructionSetting' }),
                }
              },
              {
                path: 'management-construction-name-setting',
                name: 'ManagementConstructionNameSetting',
                component: () => import('@/views/construction/setting/ManagementConstructionNameSetting.vue'),
                meta: {
                  backRouteFor: () => ({ name: 'ConstructionSetting' }),
                }
              },
              {
                path: 'photo-organizer',
                name: 'PhotoOrganizer',
                component: () => import('@/views/construction/photoOrganizer/PhotoOrganizer.vue'),
                meta: {
                  backRouteFor: (route: RouteLocationNormalized) => (
                    route.query.open_from === 'constructionCreated'
                      ? { name: 'ConstructionList', query: { construction_id: route.params.cloudConstructionId } }
                      : { name: 'ConstructionSetting', params: { cloudConstructionId: route.params.cloudConstructionId } }
                  ),
                },
              },
            ]
          },
          {
            path: 'amnimo',
            name: 'AmnimoCameraList',
            component: () => import('@/views/construction/amnimo/Amnimo.vue'),
            meta: {
              isPublic: true,
              ...defaultMetaProps,
            },
          },
          {
            path: 'lives',
            name: 'KuraemonLiveList',
            component: () => import('@/views/construction/live/LiveList.vue'),
            meta: {
              ...defaultMetaProps,
              layout: 'sideservice',
              backRouteFor: (route: RouteLocationNormalized) => ({ name: 'ConstructionList', query: { construction_id: route.params.cloudConstructionId } }),
            },
            children: [
              {
                path: ':cameraId',
                name: 'KuraemonLiveDetail',
                component: () => import('@/views/construction/live/LiveDetail.vue'),
                meta: {
                  ...defaultMetaProps,
                  layout: 'sideservice',
                  backRouteFor: (route: RouteLocationNormalized) => ({
                    name: 'KuraemonLiveList',
                    params: { cloudConstructionId: route.params.cloudConstructionId },
                    query: {
                      ...route.query,
                      cameraId: route.params.cameraId,
                    }
                  }),
                },
              }
            ]
          },
          // {
          //   path: 'lives2/:cameraId',
          //   name: 'KuraemonLiveDetail',
          //   component: () => import('@/views/construction/live/LiveDetail.vue'),
          //   meta: {
          //     ...defaultMetaProps,
          //     layout: 'empty',
          //     backRouteFor: (route: RouteLocationNormalized) => ({
          //       name: 'KuraemonLiveList',
          //       params: { cloudConstructionId: route.params.cloudConstructionId },
          //       query: {
          //         ...route.query,
          //         cameraId: route.params.cameraId,
          //       }
          //     }),
          //   },
          // },
        ],
      },
    ],
  }
]

const router = createRouter({
  history: createWebHashHistory(),
  routes: routes,
})
export default router
